import {
  REPORT_NOTE_LIMIT,
  REPORT_REASONS,
  REPORT_STATUSES,
  answerResponseSchema,
} from '@oefy/core';
import { z } from 'zod';
import { pageFields, pageQuery, schoolGroupSchema, timestampSchema } from './common';
import { contentStatusSchema, questionTypeSchema } from './content';

export const reportReasonSchema = z.enum(REPORT_REASONS);
export const reportStatusSchema = z.enum(REPORT_STATUSES);

export const questionReportInputSchema = z.object({
  position: z.int().min(0),
  reason: reportReasonSchema,
  note: z.string().trim().max(REPORT_NOTE_LIMIT).optional(),
  response: answerResponseSchema.nullable().optional(),
});

export const adminReportListQuerySchema = z.object({
  status: reportStatusSchema.optional(),
  questionId: z.uuid().optional(),
  ...pageQuery,
});

export const adminReportSchema = z.object({
  id: z.uuid(),
  reason: reportReasonSchema,
  note: z.string().nullable(),
  answer: z.string().nullable(),
  status: reportStatusSchema,
  schoolGroup: schoolGroupSchema,
  createdAt: timestampSchema,
  resolvedAt: timestampSchema.nullable(),
  resolvedBy: z.string().nullable(),
  parent: z.object({ id: z.uuid(), email: z.string() }).nullable(),
  question: z.object({
    id: z.uuid(),
    prompt: z.string(),
    type: questionTypeSchema,
    status: contentStatusSchema,
    subjectName: z.string(),
  }),
});

export const adminReportListSchema = z.object({
  reports: z.array(adminReportSchema),
  ...pageFields,
});

export const adminReportGroupQuerySchema = z.object({
  status: reportStatusSchema.optional(),
  reason: reportReasonSchema.optional(),
  subjectKey: z.string().min(1).max(40).optional(),
  schoolGroup: z.coerce.number().pipe(schoolGroupSchema).optional(),
  ...pageQuery,
});

export const adminReportGroupSchema = z.object({
  question: z.object({
    id: z.uuid(),
    prompt: z.string(),
    type: questionTypeSchema,
    status: contentStatusSchema,
    subjectName: z.string(),
    schoolGroup: schoolGroupSchema,
    thumbnailKey: z.string().nullable(),
    correctAnswer: z.string(),
  }),
  open: z.int().min(0),
  latestAt: timestampSchema,
  reasons: z.array(z.object({ reason: reportReasonSchema, count: z.int().min(1) })),
  reports: z.array(adminReportSchema),
});

export const adminReportGroupListSchema = z.object({
  groups: z.array(adminReportGroupSchema),
  reports: z.int().min(0),
  ...pageFields,
});

export const resolvedReportsSchema = z.object({ resolved: z.int().min(0) });

export const reportParamsSchema = z.object({ reportId: z.uuid() });

export type QuestionReportInput = z.infer<typeof questionReportInputSchema>;
export type AdminReportListQuery = z.infer<typeof adminReportListQuerySchema>;
export type AdminReport = z.infer<typeof adminReportSchema>;
export type AdminReportList = z.infer<typeof adminReportListSchema>;
export type AdminReportGroupQuery = z.infer<typeof adminReportGroupQuerySchema>;
export type AdminReportGroup = z.infer<typeof adminReportGroupSchema>;
export type AdminReportGroupList = z.infer<typeof adminReportGroupListSchema>;
